import { Component, OnInit, ViewChild, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MessageService, MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { Textarea } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { FileUploadModule, FileUpload } from 'primeng/fileupload';
import { InputNumber } from 'primeng/inputnumber';
import { MultiSelectModule } from 'primeng/multiselect';
import { DialogModule } from 'primeng/dialog';
import { RegistryTableComponent } from '../../../../core/registry-table/registry-table.component';
import { ColumnConfig } from '../../dashboard/table-config';
import { 
  ContractAction, 
  ContractActionType,
  ContractActionStatus,
  CONTRACT_ACTION_TYPE_LABELS,
  CONTRACT_ACTION_STATUS_LABELS,
  getActionStatusSeverity
} from '../../../models/contract-action.model';
import { ContractInvolvedEntity } from '../../../models/contract.model';
import { ContractActionService, CreateActionRequest } from '../../../services/contract-action.service';
import { ContractRegistryService } from '../../../services/contract-registry.service';
import { DocumentTemplateV2Service } from '../../../services/document-template-v2.service';
import { DocumentAssetSummary } from '../../../models/document-asset.model';
import { isSelectableTemplate, toTemplateSelectionRow } from '../../../models/document-asset-labels';
import { NavigationContextService } from '../../../../core/services/navigation-context.service';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { FileValidationService } from '../../../../core/services/file-validation.service';
import { FormDropdownService, FormDropdownItem } from '../../../../core/services/form-dropdown.service';
import { AxaEntity, AxaEntityService } from '../../../../core/services/axa-entity.service';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { addSolCodeToFileName } from '../../../../core/utils/file-name.utils';

@Component({
  selector: 'app-contract-action-detail',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TagModule,
    ProgressSpinnerModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    InputNumber,
    Textarea,
    Toast,
    BreadcrumbModule,
    TooltipModule,
    CardModule,
    FileUploadModule,
    DialogModule,
    RegistryTableComponent
  ],
  templateUrl: './contract-action-detail.component.html',
  styleUrls: ['./contract-action-detail.component.scss']
})
export class ContractActionDetailComponent implements OnInit {
  @ViewChild('signedDocUpload') signedDocUpload?: FileUpload;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  private messageService = inject(MessageService);
  private contractActionService = inject(ContractActionService);
  private contractRegistryService = inject(ContractRegistryService);
  private documentTemplateV2Service = inject(DocumentTemplateV2Service);
  private navigationContext = inject(NavigationContextService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private fileValidationService = inject(FileValidationService);
  private formDropdownService = inject(FormDropdownService);
  private axaEntityService = inject(AxaEntityService);
  private professionalsService = inject(ProfessionalsService);

  // Edit mode signals
  isEditModeGenerali = signal(false);
  isEditModeSpecific = signal(false);
  isEditModeTemplate = signal(false);

  // Computed: true if any section is in edit mode
  hasAnySectionInEditMode = computed(() => 
    this.isEditModeGenerali() || this.isEditModeSpecific() || this.isEditModeTemplate()
  );

  // Backup for cancel
  private actionBackup: ContractAction | null = null;
  private templateBackup: string | null = null;

  isLoading = signal(true);
  breadcrumbItems = signal<MenuItem[]>([]);
  contractId = signal<string>('');
  actionId = signal<string>('');
  
  action = signal<ContractAction | null>(null);
  solCode = signal<string | null>(null);
  templateName = signal<string>('');
  currentEntities = signal<ContractInvolvedEntity[]>([]);
  
  // Document upload
  signedDocumentUrl = signal<string | null>(null);
  isUploadingSignedDoc = signal(false);
  uploadErrored = signal(false);

  // Template selection dialog
  showTemplateDialog = signal(false);
  templateTableData = signal<any[]>([]);
  templateColumns: ColumnConfig[] = [
    { title: 'Nome Template', field: 'name', type: 'text', sortable: true, filterType: 'text' },
    { title: 'Descrizione', field: 'description', type: 'text', filterType: 'text' },
    { title: 'Azioni', type: 'action', filterType: 'none' }
  ];

  // Entity options for EXTEND_PARTIES
  entityOptions = signal<AxaEntity[]>([]);

  // Currency options for MODIFY_ECONOMICS
  currencyOptions = signal<FormDropdownItem[]>([]);

  // Computed: Determine if action is editable (only DRAFT, CANCELED is read-only like ACTIVE)
  isActionEditable = computed(() => {
    const status = this.action()?.status;
    return status === ContractActionStatus.DRAFT;
  });

  // Computed: Can activate (must be DRAFT with signed document, except RENEW_CONTRACT)
  canActivateAction = computed(() => {
    const status = this.action()?.status;
    const actionType = this.action()?.actionType;
    
    // RENEW_CONTRACT can be activated without document
    if (actionType === 'RENEW_CONTRACT') {
      return status === ContractActionStatus.DRAFT;
    }
    
    // Other action types require signed document
    return status === ContractActionStatus.DRAFT && !!this.signedDocumentUrl();
  });

  // Computed: tooltip for activate button
  activateActionTooltip = computed(() => {
    const actionType = this.action()?.actionType;
    
    if (!this.isActionEditable()) {
      return 'L\'azione è già attiva';
    }
    if (this.action()?.status === ContractActionStatus.ACTIVE) {
      return 'L\'azione è già attiva';
    }
    
    // RENEW_CONTRACT doesn't require document
    if (actionType === 'RENEW_CONTRACT') {
      return 'Attiva l\'azione per renderla operativa';
    }
    
    if (!this.signedDocumentUrl()) {
      return 'Carica il documento firmato prima di attivare l\'azione';
    }
    return 'Attiva l\'azione per renderla operativa';
  });

  // Computed: decoded action type label
  actionTypeLabel = computed(() => {
    const actionType = this.action()?.actionType;
    if (!actionType) return 'N/A';
    return CONTRACT_ACTION_TYPE_LABELS[actionType] || actionType;
  });

  // Computed: decoded action status label
  actionStatusLabel = computed(() => {
    const status = this.action()?.status;
    return status ? CONTRACT_ACTION_STATUS_LABELS[status] : 'N/A';
  });

  // Computed: action status severity
  actionStatusSeverity = computed(() => {
    const status = this.action()?.status;
    return status ? getActionStatusSeverity(status) : 'secondary';
  });

  ngOnInit(): void {
    this.titleService.setTitle('Dettaglio - PalEasy');

    this.route.params.subscribe(params => {
      const contractId = params['contractId'];
      const actionId = params['actionId'];
      
      if (contractId && actionId) {
        this.contractId.set(contractId);
        this.actionId.set(actionId);
        this.setupBreadcrumb();
        this.loadContractDetails();
        this.loadActionDetails();
        this.loadDropdowns();
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Parametri mancanti nella URL',
          life: 3000
        });
        this.goBack();
      }
    });
  }

  private loadDropdowns(): void {
    // Load entity options (if needed for EXTEND_PARTIES)
    this.axaEntityService.getAll().subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.entityOptions.set(response.data);
        }
      },
      error: (error) => console.error('Error loading entities:', error)
    });

    // Load currency options (if needed for MODIFY_ECONOMICS)
    this.formDropdownService.getFormDropdownValues('CURRENCY', 'CURRENCY').subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.currencyOptions.set(response.data);
        }
      },
      error: (error) => console.error('Error loading currencies:', error)
    });
  }

  private loadContractDetails(): void {
    this.contractRegistryService.getContractById(this.contractId()).subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          const professionalId = response.data.generalInfo?.professionalId;
          if (professionalId) {
            this.professionalsService.getProfessionals(0, 1, undefined, { id: professionalId }).subscribe({
              next: (professionalResponse) => {
                this.solCode.set(professionalResponse?.data?.content?.[0]?.solCode ?? null);
              },
              error: () => this.solCode.set(null),
            });
          }
          if (response.data.generalInfo?.involvedEntities) {
            this.currentEntities.set(
              this.normalizeActionInvolvedEntities(response.data.generalInfo.involvedEntities),
            );
          }
        }
      },
      error: (error) => {
        console.error('Error loading contract involved entities:', error);
      }
    });
  }

  private setupBreadcrumb(): void {
    this.breadcrumbItems.set([
      { label: 'Dashboard', routerLink: '/axa/professionals-dashboard' },
      { label: 'Registro Contratti', routerLink: `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry` },
      { label: `Contratto ${this.contractId()}`, routerLink: `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry/${this.contractId()}` },
      { label: 'Dettaglio Azione' }
    ]);
  }

  private loadActionDetails(): void {
    this.isLoading.set(true);
    
    this.contractActionService.getActionById(this.contractId(), +this.actionId()).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          // Convert date strings to Date objects for datepicker compatibility
          const actionData = { ...response.data };
          if (actionData.newEndDate && typeof actionData.newEndDate === 'string') {
            actionData.newEndDate =
              this.contractRegistryService.parseContractDate(actionData.newEndDate) ?? undefined;
          }
          actionData.newInvolvedEntities = this.normalizeActionInvolvedEntities(
            actionData.newInvolvedEntities,
          );
          this.action.set(actionData);
          
          // Popola l'URL del documento firmato se presente
          // Il backend può mandare sia signedDocumentUrl che s3Url
          const documentUrl = (response.data as any).s3Url || response.data.signedDocumentUrl;
          if (documentUrl) {
            this.signedDocumentUrl.set(documentUrl);
            this.uploadErrored.set(false);
          } else {
            // Fallback: cerca nei generatedDocuments un documento con categoria SIGNED
            const signedDoc = response.data.generatedDocuments?.find(
              doc => doc.documentCategory === 'SIGNED' || doc.documentCategory === 'SIGNED_DOCUMENT'
            );
            if (signedDoc) {
              this.signedDocumentUrl.set(signedDoc.fileUrl);
              this.uploadErrored.set(false);
            }
            // Non resettare a null se non troviamo nulla, mantieni il valore esistente
          }
          
          // Se ha un template, carica il nome
          if (response.data.selectedTemplateId) {
            this.loadTemplateName(response.data.selectedTemplateId);
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: response.message || 'Errore durante il caricamento dell\'azione',
            life: 5000
          });
          this.goBack();
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('❌ Error loading action:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il caricamento dell\'azione',
          life: 5000
        });
        this.isLoading.set(false);
        this.goBack();
      }
    });
  }

  private loadTemplateName(businessKey: string): void {
    this.documentTemplateV2Service.getByBusinessKey(businessKey).subscribe({
      next: (asset) => {
        if (asset) {
          this.templateName.set(asset.name || asset.businessKey);
        } else {
          this.templateName.set('Template non trovato');
        }
      },
      error: (error) => {
        console.error('❌ Error loading template name:', error);
        this.templateName.set('Template non trovato');
      }
    });
  }

  goBack(): void {
    this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry/${this.contractId()}`]);
  }

  private normalizeActionInvolvedEntities(
    value: ContractInvolvedEntity[] | string[] | null | undefined,
  ): ContractInvolvedEntity[] {
    if (!value || !Array.isArray(value)) {
      return [];
    }

    return value.map((item) => {
      if (typeof item === 'string') {
        return {
          entityCode: item,
          signatoryName: '',
          signatoryRole: '',
        };
      }

      return {
        entityCode: item?.entityCode || '',
        signatoryName: item?.signatoryName || '',
        signatoryRole: item?.signatoryRole || '',
      };
    });
  }

  getActionInvolvedEntities(): ContractInvolvedEntity[] {
    return this.action()?.newInvolvedEntities || [];
  }

  private getCurrentContractEntityCodes(): string[] {
    return this.currentEntities()
      .map((entity) => entity.entityCode?.trim())
      .filter((entityCode): entityCode is string => !!entityCode);
  }

  private getExtendableEntityOptions(): AxaEntity[] {
    const currentContractEntityCodes = new Set(this.getCurrentContractEntityCodes());
    return this.entityOptions().filter((option) => !currentContractEntityCodes.has(option.entityCode));
  }

  getAvailableInvolvedEntityOptions(currentIndex: number): AxaEntity[] {
    const selectedEntities = this.getActionInvolvedEntities()
      .map((item, index) => (index === currentIndex ? null : item?.entityCode))
      .filter((value) => value !== null && value !== '');

    const currentValue = this.getActionInvolvedEntities()[currentIndex]?.entityCode?.trim();

    return this.getExtendableEntityOptions().filter((option) => {
      if (currentValue && option.entityCode === currentValue) {
        return true;
      }

      return !selectedEntities.includes(option.entityCode);
    });
  }

  canAddMoreInvolvedEntities(): boolean {
    return this.getActionInvolvedEntities().length < this.getExtendableEntityOptions().length;
  }

  addInvolvedEntity(): void {
    const currentAction = this.action();
    if (!currentAction || !this.canAddMoreInvolvedEntities()) {
      return;
    }

    this.action.set({
      ...currentAction,
      newInvolvedEntities: [
        ...this.getActionInvolvedEntities(),
        { entityCode: '', signatoryName: '', signatoryRole: '' },
      ],
    });
  }

  removeInvolvedEntity(index: number): void {
    const currentAction = this.action();
    if (!currentAction || this.getActionInvolvedEntities().length <= 1) {
      return;
    }

    this.action.set({
      ...currentAction,
      newInvolvedEntities: this.getActionInvolvedEntities().filter((_, itemIndex) => itemIndex !== index),
    });
  }

  getInvolvedEntityLabel(entityCode: string | null | undefined): string {
    const option = this.entityOptions().find((item) => item.entityCode === entityCode);
    return option?.entityDescription || entityCode || '-';
  }

  onInvolvedEntityChange(index: number): void {
    const currentAction = this.action();
    const entities = currentAction?.newInvolvedEntities || [];
    const selectedEntity = this.entityOptions().find(
      (entity) => entity.entityCode === entities[index]?.entityCode,
    );

    if (!currentAction) {
      return;
    }

    this.action.set({
      ...currentAction,
      newInvolvedEntities: entities.map((entity, entityIndex) =>
        entityIndex === index
          ? {
              ...entity,
              signatoryName: selectedEntity?.signatoryName || '',
              signatoryRole: selectedEntity?.signatoryRole || '',
            }
          : entity,
      ),
    });
  }

  private hasValidExtendPartiesSelection(): boolean {
    if (this.getActionInvolvedEntities().length === 0) {
      return false;
    }

    const entityCodes = this.getActionInvolvedEntities()
      .map((entity) => entity.entityCode?.trim())
      .filter((entityCode): entityCode is string => !!entityCode);

    return entityCodes.length === this.getActionInvolvedEntities().length
      && new Set(entityCodes).size === entityCodes.length;
  }

  // ========== EDIT MODE MANAGEMENT ==========

  toggleEditGenerali(): void {
    if (!this.isEditModeGenerali()) {
      // Entering edit mode - save backup
      this.actionBackup = JSON.parse(JSON.stringify(this.action()));
      this.isEditModeGenerali.set(true);
    } else {
      // Exiting edit mode - show confirmation dialog
      this.confirmDialogService.confirmCancelEdit(() => {
        // Restore backup (cancel)
        if (this.actionBackup) {
          this.action.set(this.actionBackup);
        }
        this.isEditModeGenerali.set(false);
      });
    }
  }

  toggleEditSpecific(): void {
    if (!this.isEditModeSpecific()) {
      // Entering edit mode - save backup
      this.actionBackup = JSON.parse(JSON.stringify(this.action()));
      this.isEditModeSpecific.set(true);
    } else {
      // Exiting edit mode - show confirmation dialog
      this.confirmDialogService.confirmCancelEdit(() => {
        // Restore backup (cancel)
        if (this.actionBackup) {
          this.action.set(this.actionBackup);
        }
        this.isEditModeSpecific.set(false);
      });
    }
  }

  toggleEditTemplate(): void {
    if (!this.isEditModeTemplate()) {
      // Entering edit mode - save backup
      this.templateBackup = this.action()?.selectedTemplateId || null;
      this.isEditModeTemplate.set(true);
    } else {
      // Exiting edit mode - show confirmation dialog
      this.confirmDialogService.confirmCancelEdit(() => {
        // Restore backup (cancel)
        if (this.templateBackup !== null && this.action()) {
          const updatedAction = {
            ...this.action()!,
            selectedTemplateId: this.templateBackup
          };
          this.action.set(updatedAction);
          // Reload template name
          if (this.templateBackup) {
            this.loadTemplateName(this.templateBackup);
          }
        }
        this.isEditModeTemplate.set(false);
      });
    }
  }

  saveTemplate(): void {
    const currentAction = this.action();
    if (!currentAction || !currentAction.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Dati azione non disponibili',
        life: 3000
      });
      return;
    }

    if (!currentAction.selectedTemplateId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Seleziona un template prima di salvare',
        life: 3000
      });
      return;
    }

    const contractId = this.contractId();
    const actionId = currentAction.id;

    // Build update request with template field
    const request: Partial<CreateActionRequest> = {
      actionType: currentAction.actionType,
      description: currentAction.actionName,
      selectedTemplateId: currentAction.selectedTemplateId
    };

    this.contractActionService.updateAction(contractId, actionId, request).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Template aggiornato con successo',
          life: 3000
        });
        this.isEditModeTemplate.set(false);
        this.templateBackup = null;
      },
      error: (error) => {
        console.error('Error updating template:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il salvataggio del template',
          life: 5000
        });
      }
    });
  }

  openTemplateDialog(): void {
    // Load templates for the action type from v2 DocumentAsset catalog.
    const domainType = this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
    this.documentTemplateV2Service
      .listMasterTemplates(domainType, 'CONTRACTS')
      .subscribe({
        next: (assets: DocumentAssetSummary[]) => {
          this.templateTableData.set(
            assets.filter(isSelectableTemplate).map(toTemplateSelectionRow),
          );
          this.showTemplateDialog.set(true);
        },
        error: (error) => {
          console.error('Error loading templates (v2):', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore durante il caricamento dei template',
            life: 5000
          });
        }
      });
  }

  closeTemplateDialog(): void {
    this.showTemplateDialog.set(false);
  }

  selectTemplate(template: any): void {
    // Il template passato può provenire da `templateTableData` (dove `id` è il
    // businessKey del DocumentAsset). Preferiamo `businessKey` se presente,
    // altrimenti `id`.
    const templateData = {
      label: template.name,
      value: (template.businessKey ?? template.id) as string,
    };
    
    const currentAction = this.action();
    if (currentAction) {
      const updatedAction = {
        ...currentAction,
        selectedTemplateId: templateData.value
      };
      this.action.set(updatedAction);
      this.loadTemplateName(templateData.value);
    }
    
    this.closeTemplateDialog();
    
    this.messageService.add({
      severity: 'success',
      summary: 'Template selezionato',
      detail: `${templateData.label} è stato selezionato come Template Documento`,
      life: 3000
    });
  }

  saveSection(section: 'generali' | 'specific'): void {
    const currentAction = this.action();
    if (!currentAction || !currentAction.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Dati azione non disponibili',
        life: 3000
      });
      return;
    }

    // Build update request with proper field mapping
    const request: Partial<CreateActionRequest> = {
      actionType: currentAction.actionType,
      actionName: currentAction.actionName,
      description: currentAction.description,
      notes: currentAction.notes
    };

    // Map type-specific fields to API request format
    switch (currentAction.actionType) {
      case ContractActionType.MODIFY_DURATION:
        if (currentAction.newEndDate) {
          request.newEndDate =
            this.contractRegistryService.formatContractDateForApi(currentAction.newEndDate) ?? undefined;
        }
        break;
      
      case ContractActionType.MODIFY_SUBJECT:
        request.newContractSubject = currentAction.newContractSubject;
        break;
      
      case ContractActionType.MODIFY_ECONOMICS:
        request.newCurrency = currentAction.newCurrency;
        request.newTotalContractValue = currentAction.newTotalContractValue;
        request.newAnnualValue = currentAction.newAnnualValue;
        break;
      
      case ContractActionType.EXTEND_PARTIES:
        if (!this.hasValidExtendPartiesSelection()) {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Inserisci almeno una entity valida senza duplicazioni.',
            life: 3000
          });
          return;
        }
        request.newInvolvedEntities = this.getActionInvolvedEntities().map((entity) => ({
          entityCode: entity.entityCode?.trim() || '',
          signatoryName: entity.signatoryName?.trim() || '',
          signatoryRole: entity.signatoryRole?.trim() || '',
        }));
        break;
      
      case ContractActionType.TERMINATION:
        request.terminationReason = currentAction.terminationReason;
        break;
      
      case ContractActionType.RENEW_CONTRACT:
        // RENEW_CONTRACT doesn't have additional API fields
        break;
    }

    // Call update API
    this.contractActionService.updateAction(this.contractId(), +this.actionId(), request).subscribe({
      next: (response) => {
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Modifiche salvate con successo',
            life: 3000
          });
          // Exit edit mode
          this.toggleEditSection(section);
          // Reload to get updated data
          this.loadActionDetails();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: response.message || 'Errore durante il salvataggio',
            life: 5000
          });
        }
      },
      error: (error) => {
        console.error('❌ Error saving section:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il salvataggio delle modifiche',
          life: 5000
        });
      }
    });
  }

  private toggleEditSection(section: 'generali' | 'specific'): void {
    switch (section) {
      case 'generali':
        this.isEditModeGenerali.set(false);
        break;
      case 'specific':
        this.isEditModeSpecific.set(false);
        break;
    }
  }

  // ========== DOCUMENT MANAGEMENT ==========

  async onSignedDocumentSelect(event: any): Promise<void> {
    const file = event.files[0];
    if (!file) return;

    const contractId = this.contractId();
    const actionId = +this.actionId();

    if (!contractId || !actionId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto o azione non disponibile',
        life: 3000
      });
      event.files = [];
      return;
    }

    // Validate file
    const validation = await this.fileValidationService.validateDocument(file);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: '🔒 Validazione File Fallita',
        detail: validation.error || 'Il file non ha superato i controlli di sicurezza',
        life: 6000
      });
      
      if (validation.details) {
        console.warn('File validation failed:', validation.details);
      }
      
      event.files = [];
      return;
    }

    this.isUploadingSignedDoc.set(true);
    this.uploadErrored.set(false);
    
    // Upload del file direttamente - il backend gestisce l'upload su S3
    this.contractActionService.uploadSignedDocument(contractId, actionId, file).subscribe({
      next: (response) => {
        console.log('✅ Upload signed document response:', response);
        this.isUploadingSignedDoc.set(false);
        
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Documento firmato caricato con successo',
            life: 3000
          });
          
          // Aggiorna direttamente con i dati del response invece di ricaricare tutto
          if (response.data) {
            // Aggiorna l'action con i dati tornati
            const actionData = { ...response.data };
            if (actionData.newEndDate && typeof actionData.newEndDate === 'string') {
              actionData.newEndDate =
                this.contractRegistryService.parseContractDate(actionData.newEndDate) ?? undefined;
            }
            this.action.set(actionData);
            
            // Aggiorna l'URL del documento firmato
            if (response.data.signedDocumentUrl) {
              this.signedDocumentUrl.set(response.data.signedDocumentUrl);
              this.uploadErrored.set(false);
            } else {
              // Fallback: usa il nome del file come placeholder
              this.signedDocumentUrl.set(file.name);
              this.uploadErrored.set(false);
            }
          }
          
          event.files = [];
          if (this.signedDocUpload) {
            this.signedDocUpload.clear();
          }
        } else {
          throw new Error(response.message || 'Errore durante il caricamento');
        }
      },
      error: (error) => {
        console.error('❌ Error uploading signed document:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il caricamento del documento',
          life: 5000
        });
        this.isUploadingSignedDoc.set(false);
        this.uploadErrored.set(true);
        this.signedDocumentUrl.set(null);
        if (this.signedDocUpload) {
          this.signedDocUpload.clear();
        }
      }
    });
  }

  downloadSignedDocument(): void {
    // TODO: Implement document download
    this.messageService.add({
      severity: 'info',
      summary: 'Funzionalità in sviluppo',
      detail: 'Il download del documento sarà disponibile a breve',
      life: 3000
    });
  }

  retryUpload(): void {
    this.uploadErrored.set(false);
    this.messageService.add({
      severity: 'info',
      summary: 'Pronto per nuovo caricamento',
      detail: 'Seleziona nuovamente il documento.',
      life: 3000
    });
  }

  deleteSignedDocument(): void {
    const contractId = this.contractId();
    const actionId = +this.actionId();

    if (!contractId || !actionId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto o azione non disponibile',
        life: 3000
      });
      return;
    }

    this.confirmDialogService.confirm({
      header: 'Conferma eliminazione',
      message: 'Sei sicuro di voler eliminare il documento firmato?',
      accept: () => {
        this.contractActionService.deleteSignedDocument(contractId, actionId).subscribe({
          next: (response) => {
            if (response.status) {
              this.signedDocumentUrl.set(null);
              this.messageService.add({
                severity: 'success',
                summary: 'Documento eliminato',
                detail: 'Documento firmato eliminato con successo',
                life: 3000
              });
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: response.message || 'Errore durante l\'eliminazione del documento',
                life: 5000
              });
            }
          },
          error: (error) => {
            console.error('❌ Error deleting signed document:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: error?.error?.message || 'Errore durante l\'eliminazione del documento',
              life: 5000
            });
          }
        });
      }
    });
  }

  // ========== ACTION ACTIVATION ==========

  activateAction(): void {
    const contractId = this.contractId();
    const actionId = +this.actionId();
    const actionType = this.action()?.actionType;

    if (!contractId || !actionId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto o azione non disponibile',
        life: 3000
      });
      return;
    }

    // RENEW_CONTRACT doesn't require signed document
    if (actionType !== 'RENEW_CONTRACT' && !this.signedDocumentUrl()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: "Carica il documento firmato prima di attivare l'azione",
        life: 4000
      });
      return;
    }

    this.confirmDialogService.confirm({
      header: 'Conferma attivazione',
      message: 'Sei sicuro di voler attivare questa azione? Una volta attivata, non sarà più possibile modificare le informazioni.',
      acceptLabel: 'Sì, attiva',
      rejectLabel: 'Annulla',
      accept: () => {
        this.contractActionService.activateAction(contractId, actionId).subscribe({
          next: (response) => {
            if (response.status) {
              this.messageService.add({
                severity: 'success',
                summary: 'Azione attivata',
                detail: 'L\'azione è stata attivata con successo',
                life: 4000
              });
              // Reload action details to get updated status
              this.loadActionDetails();
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: response.message || 'Errore durante l\'attivazione dell\'azione',
                life: 5000
              });
            }
          },
          error: (error) => {
            console.error('❌ Error activating action:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: error?.error?.message || 'Errore durante l\'attivazione dell\'azione',
              life: 5000
            });
          }
        });
      }
    });
  }

  // ========== NAVIGATION ==========

  editAction(): void {
    this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry/${this.contractId()}/actions/${this.actionId()}/edit`]);
  }

  /**
   * Naviga al contratto rinnovato
   * Carica i dati del contratto, verifica lo status e naviga al form (DRAFT) o detail (altri stati)
   * Passa i dati come state per evitare chiamata API ridondante
   */
  navigateToContract(contractId: number): void {
    this.isLoading.set(true);
    const contractIdStr = String(contractId);
    
    this.contractRegistryService.getContractById(contractIdStr).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        if (response.status && response.data) {
          const contract = response.data;
          const status = contract.generalInfo?.status || 'DRAFT';
          
          console.log('[navigateToContract] Contract ID:', contractIdStr, 'Status:', status);
          
          // Se DRAFT o GENERATED -> vai al form wizard in edit mode
          // Altrimenti -> vai al detail (read-only)
          if (status === 'DRAFT' || status === 'GENERATED') {
            console.log('[navigateToContract] Navigating to wizard (DRAFT/GENERATED)');
            this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contract-registration-wizard/${contractIdStr}`], {
              state: { contract }
            });
          } else {
            console.log('[navigateToContract] Navigating to detail (not DRAFT)');
            this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry/${contractIdStr}`], {
              state: { contract }
            });
          }
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare il contratto'
        });
        console.error('Error loading contract:', error);
      }
    });
  }

  getActionTypeLabel(): string {
    return this.actionTypeLabel();
  }

  getActionStatusLabel(): string {
    return this.actionStatusLabel();
  }

  getActionStatusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    return this.actionStatusSeverity();
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    
    try {
      const d = new Date(date);
      return d.toLocaleDateString('it-IT', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    } catch {
      return 'Data non valida';
    }
  }

  showModifyDurationFields(): boolean {
    return this.action()?.actionType === ContractActionType.MODIFY_DURATION;
  }

  showModifySubjectFields(): boolean {
    return this.action()?.actionType === ContractActionType.MODIFY_SUBJECT;
  }

  showModifyFeesFields(): boolean {
    return this.action()?.actionType === ContractActionType.MODIFY_ECONOMICS;
  }

  showExtendPartiesFields(): boolean {
    return this.action()?.actionType === ContractActionType.EXTEND_PARTIES;
  }

  showRenewFields(): boolean {
    return this.action()?.actionType === ContractActionType.RENEW_CONTRACT;
  }

  showTerminateFields(): boolean {
    return this.action()?.actionType === ContractActionType.TERMINATION;
  }

  generateDocument(): void {
    const action = this.action();
    if (!action || !action.selectedTemplateId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Nessun template selezionato per questa azione',
        life: 3000
      });
      return;
    }

    this.documentTemplateV2Service
      .renderDocx(action.selectedTemplateId, {
        contextId: action.id!.toString(),
        contextType: 'ACTION',
        outputFormat: 'DOCX',
      })
      .subscribe({
        next: (blob: Blob) => {
          const actionTypeName = this.getActionTypeLabel().toLowerCase().replace(/\s+/g, '_');
          const filename = `azione_${actionTypeName}_${action.id}_${new Date().getTime()}.docx`;

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          this.messageService.add({
            severity: 'success',
            summary: 'Documento generato',
            detail: 'Il documento dell\'azione è stato generato con successo',
            life: 4000
          });
        },
        error: (error) => {
          console.error('❌ Error rendering template (v2, action):', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: error?.error?.message || 'Errore durante la generazione del documento',
            life: 5000
          });
        }
      });
  }

  completeAction(): void {
    this.confirmDialogService.confirm({
      header: 'Conferma completamento',
      message: 'Sei sicuro di voler completare questa azione? L\'operazione non può essere annullata.',
      accept: () => {
        this.contractActionService.completeAction(this.contractId(), +this.actionId()).subscribe({
          next: (response) => {
            if (response.status) {
              this.messageService.add({
                severity: 'success',
                summary: 'Azione completata',
                detail: 'L\'azione è stata completata con successo',
                life: 3000
              });
              this.loadActionDetails();
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: response.message || 'Errore durante il completamento dell\'azione',
                life: 5000
              });
            }
          },
          error: (error) => {
            console.error('❌ Error completing action:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: error?.error?.message || 'Errore durante il completamento dell\'azione',
              life: 5000
            });
          }
        });
      }
    });
  }

  deleteAction(): void {
    this.confirmDialogService.confirm({
      header: 'Conferma eliminazione',
      message: 'Sei sicuro di voler eliminare questa azione? L\'operazione non può essere annullata.',
      accept: () => {
        this.contractActionService.deleteAction(this.contractId(), +this.actionId()).subscribe({
          next: (response) => {
            if (response.status) {
              this.messageService.add({
                severity: 'success',
                summary: 'Azione eliminata',
                detail: 'L\'azione è stata eliminata con successo',
                life: 3000
              });
              this.goBack();
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: response.message || 'Errore durante l\'eliminazione dell\'azione',
                life: 5000
              });
            }
          },
          error: (error) => {
            console.error('❌ Error deleting action:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: error?.error?.message || 'Errore durante l\'eliminazione dell\'azione',
              life: 5000
            });
          }
        });
      }
    });
  }

  canEdit(): boolean {
    return this.action()?.status === ContractActionStatus.DRAFT;
  }

  canComplete(): boolean {
    return this.action()?.status === ContractActionStatus.DRAFT;
  }

  canDelete(): boolean {
    return this.action()?.status === ContractActionStatus.DRAFT;
  }

  hasTemplate(): boolean {
    return !!this.action()?.selectedTemplateId;
  }

  /**
   * Formatta la dimensione di un file in bytes in un formato leggibile
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  // ========== UNSAVED CHANGES GUARD ==========

  canDeactivate(): boolean {
    if (this.hasAnySectionInEditMode()) {
      return confirm('Ci sono modifiche non salvate. Vuoi davvero uscire dalla pagina?');
    }
    return true;
  }

  getDropdownLabel(options: FormDropdownItem[], valueKey: string | undefined): string {
    if (!valueKey) return 'N/A';
    const option = options.find(opt => opt.valueKey === valueKey);
    return option?.valueDescription || valueKey;
  }
}
